import React, { useCallback, useEffect, useRef, useState } from 'react';

type ScrollDirection = 'forward' | 'backward';

type Props = {
  text: string;
  className?: string;
  textClassName?: string; // Tailwind text color class (e.g., 'text-orange', 'text-main-400')
  scrollSpeed?: number; // pixels per second
  pauseDuration?: number; // milliseconds to pause at start/end
  startDelay?: number; // milliseconds to wait before starting animation on hover
};

export const ScrollingOverflowText = ({
  text,
  className,
  textClassName,
  scrollSpeed = 60,
  pauseDuration = 2000,
  startDelay = 600,
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [scrollOffset, setScrollOffset] = useState(0);
  const animationRef = useRef<number>();
  const lastTimeRef = useRef(0);
  const hoverStartTimeRef = useRef(0);
  const pauseStartRef = useRef<number | null>(null);
  const scrollDirectionRef = useRef<ScrollDirection>('forward');

  const resetAnimationState = useCallback(() => {
    setScrollOffset(0);
    lastTimeRef.current = 0;
    hoverStartTimeRef.current = 0;
    pauseStartRef.current = null;
    scrollDirectionRef.current = 'forward';
  }, []);

  // Check if text is overflowing using ResizeObserver
  useEffect(() => {
    const checkOverflow = () => {
      if (containerRef.current && textRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const textWidth = textRef.current.scrollWidth;
        setIsOverflowing(textWidth > containerWidth);
      }
    };

    // Small delay to ensure DOM has updated after hover state change
    const timeoutId = setTimeout(checkOverflow, 0);

    const resizeObserver = new ResizeObserver(checkOverflow);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, [text, isHovered]);

  // Animate scrolling on hover
  useEffect(() => {
    if (!isOverflowing || !isHovered) {
      resetAnimationState();
      return undefined;
    }

    const animate = (timestamp: number) => {
      if (!containerRef.current || !textRef.current) return;

      const containerWidth = containerRef.current.offsetWidth;
      const textWidth = textRef.current.scrollWidth;
      const maxScroll = textWidth - containerWidth;

      // Initialize hover start time
      if (hoverStartTimeRef.current === 0) {
        hoverStartTimeRef.current = timestamp;
      }

      // Wait for start delay
      const timeSinceHover = timestamp - hoverStartTimeRef.current;
      if (timeSinceHover < startDelay) {
        animationRef.current = requestAnimationFrame(animate);
        return;
      }

      // Initialize animation timer after delay
      if (lastTimeRef.current === 0) {
        lastTimeRef.current = timestamp;
      }

      const deltaTime = timestamp - lastTimeRef.current;
      lastTimeRef.current = timestamp;

      // Handle pause at scroll boundaries
      if (pauseStartRef.current !== null) {
        const pauseElapsed = timestamp - pauseStartRef.current;
        if (pauseElapsed < pauseDuration) {
          animationRef.current = requestAnimationFrame(animate);
          return;
        }
        pauseStartRef.current = null;
      }

      // Update scroll position
      const scrollDelta = (scrollSpeed * deltaTime) / 1000;
      setScrollOffset((currentOffset) => {
        if (scrollDirectionRef.current === 'forward') {
          const newOffset = currentOffset + scrollDelta;
          if (newOffset >= maxScroll) {
            pauseStartRef.current = timestamp;
            scrollDirectionRef.current = 'backward';
            return maxScroll;
          }
          return newOffset;
        }

        const newOffset = currentOffset - scrollDelta;
        if (newOffset <= 0) {
          pauseStartRef.current = timestamp;
          scrollDirectionRef.current = 'forward';
          return 0;
        }
        return newOffset;
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      resetAnimationState();
    };
  }, [
    isOverflowing,
    isHovered,
    scrollSpeed,
    pauseDuration,
    startDelay,
    resetAnimationState,
  ]);

  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);

  const showEllipsis = isOverflowing && !isHovered;

  return (
    <div
      ref={containerRef}
      className={className}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        position: 'relative',
        width: '100%',
        textOverflow: showEllipsis ? 'ellipsis' : 'clip',
      }}
    >
      {showEllipsis ? (
        <span ref={textRef} className={textClassName}>
          {text}
        </span>
      ) : (
        <span
          ref={textRef}
          className={textClassName}
          style={{
            display: 'inline-block',
            transform: `translateX(-${scrollOffset}px)`,
            whiteSpace: 'nowrap',
          }}
        >
          {text}
        </span>
      )}
    </div>
  );
};
